import React from 'react'
import { Counter, ContentSection } from 'sidecar-components'

import { HelpNav } from 'components/_common/HelpNav'
import { BackButton } from 'components/_common/BackButton'
import { useMemberComms } from 'hooks/useMemberComms'

/**
 * The PageHeader is featured on nearly every page. On mobile, it also provides
 * a way for the user to navigate back to the previous page and retain access to
 * important links that are in the desktop version of the GlobalNavigation.
 *
 * @param {ReactNode} children Content for below the title
 * @param {string} className Optional classes provided by the parent
 * @param {number} count Optional count for the inline counter
 * @param {string} testId Optional test class
 * @param {boolean} hideMobileBackBtn Default is false. Hides only back button
 *   on mobile. Mainly for dashboard and top level pages.
 * @param {boolean} hideMobileContent Default is false. Hides back button and
 *   help nav on mobile. Mainly for enrollment funnel.
 * @param {ReactNode} mobileContent Content for next to help nav on mobile
 * @param {boolean} redactData Whether to redact text from Heap
 * @param {string} title Title of the page
 * @param {ReactNode} topContent Content for above the title
 * @param {string} wrapperClassName Optional classes provided to the parent
 *   parent
 * @returns A PageHeader component to put at the top of a page/route
 */
export const PageHeader = ({
  children,
  className = '',
  count = 0,
  testId = '',
  redactData,
  hideMobileBackBtn,
  hideMobileContent,
  mobileContent,
  title,
  topContent,
  wrapperClassName = '',
  ...rest
}) => {
  // Pull unresolved requests count from member comms context
  const { unresolvedRequests } = useMemberComms()

  return (
    <header data-heap-redact-text={redactData} {...rest}>
      {/* Using a ContentSection to help with centering and things */}
      <ContentSection
        strong
        className={`flex flex-col !gap-4 ${className}`}
        // Adding safe area for native app. space.5 is the ContentSection's default mobile padding.
        wrapperClassName={`max-md:pt-[calc(var(--safe-top)+theme(space.5))] ${wrapperClassName}`}
        compact
      >
        {/*
          On mobile, there needs to be a back button and a couple of utility icons
          which show on the global nav on desktop. It's like a mini-global nav
        */}
        {!hideMobileContent && (
          <div className="flex items-center md:hidden print:hidden">
            {/* Back button is important for mobile navigation but not on top-level pages */}
            {!hideMobileBackBtn && <BackButton />}
            <div className="flex items-center justify-end gap-6 w-full pl-4">
              {/* This mobile content is for us to put other buttons and things
                that we'd like to be at the top of the page on mobile. */}
              {mobileContent}

              {/* Only logged in users see this header so we can say they're authenticated */}
              <HelpNav
                hasPolicy={true}
                authenticated={true}
                isMobile={true}
                unresolvedRequests={unresolvedRequests}
              />
            </div>
          </div>
        )}

        {/*
          In most cases, this topContent will just be a StepTracker but
          it's been left open for whatever we want.
        */}
        {topContent}

        {!!title && (
          <h1 className="my-0 inherit" data-testid={testId}>
            {title}
            {count > 0 && (
              <Counter className="ml-2 align-super">{count}</Counter>
            )}
          </h1>
        )}

        {/*
          Using children like this allows us to put in anything we
          want below the header (pills, subtitles, search bars, etc.)
        */}
        {children}
      </ContentSection>
    </header>
  )
}
